/**
 * Findings UI Constants
 *
 * Color and style constants for severity levels and finding statuses.
 * Used by finding cards, badges, and summary components.
 *
 * Each config uses `labelKey` for i18n translation. The `label` field
 * provides an English fallback. For translated labels in React components,
 * use the `useSeverityConfig()` / `useStatusConfig()` hooks instead.
 */

import { getPaletteColor } from "@/lib/color-palettes";
import { useTranslation } from "@/packages/utils";
import type {
  SeverityType,
  FindingStatusType,
} from "@/packages/domains/findings";

// ─── Types ───────────────────────────────────────────────────────────────────

export type SeverityConfig = {
  bg: string;
  border: string;
  dot: string;
  labelKey: string;
  /** English fallback — for translated label, use `useSeverityConfig()` */
  label: string;
};

export type StatusConfig = {
  bg: string;
  text: string;
  labelKey: string;
  /** English fallback — for translated label, use `useStatusConfig()` */
  label: string;
};

// ─── Base Config (palette-derived, resolved at access time) ─────────────────

const SEVERITY_SLOT: Record<SeverityType, number> = {
  critical: 1,
  major: 2,
  minor: 3,
  observation: 4,
};

const SEVERITY_LABELS: Record<
  SeverityType,
  { labelKey: string; label: string }
> = {
  critical: { labelKey: "findings.severity.critical", label: "Critical" },
  major: { labelKey: "findings.severity.major", label: "Major" },
  minor: { labelKey: "findings.severity.minor", label: "Minor" },
  observation: {
    labelKey: "findings.severity.observation",
    label: "Observation",
  },
};

/**
 * Returns severity color config derived from the workspace findings palette.
 * Colors are resolved at call time from CSS variables.
 */
export function getSeverityStyle(severity: SeverityType): SeverityConfig {
  const color = getPaletteColor("findings", SEVERITY_SLOT[severity] ?? 4);
  return {
    bg: `${color}0a`, // ~4% opacity background
    border: color,
    dot: color,
    ...SEVERITY_LABELS[severity],
  };
}

/**
 * Returns the severity card tint color (very low opacity) from the palette.
 */
export function getSeverityTint(severity: SeverityType): string {
  const color = getPaletteColor("findings", SEVERITY_SLOT[severity] ?? 4);
  return `${color}08`; // ~3% opacity
}

/**
 * Backward-compatible record that resolves palette colors on each property access.
 */
export const SEVERITY_COLORS: Record<SeverityType, SeverityConfig> = new Proxy(
  // Proxy seed: empty object typed for consumer inference
  {} as Record<SeverityType, SeverityConfig>,
  {
    get(_target, prop: string) {
      if (prop in SEVERITY_SLOT) {
        // Proxy get receives string|symbol; narrowed by `in` check above
        return getSeverityStyle(prop as SeverityType);
      }
      return undefined;
    },
    ownKeys() {
      return Object.keys(SEVERITY_SLOT);
    },
    getOwnPropertyDescriptor(_target, prop: string) {
      if (prop in SEVERITY_SLOT) {
        return {
          configurable: true,
          enumerable: true,
          writable: false,
          value: getSeverityStyle(prop as SeverityType),
        };
      }
      return undefined;
    },
  },
);

/**
 * Backward-compatible record for card tints that resolves from palette.
 */
export const SEVERITY_CARD_TINTS: Record<SeverityType, string> = new Proxy(
  // Proxy seed: empty object typed for consumer inference
  {} as Record<SeverityType, string>,
  {
    get(_target, prop: string) {
      if (prop in SEVERITY_SLOT) {
        // Proxy get receives string|symbol; narrowed by `in` check above
        return getSeverityTint(prop as SeverityType);
      }
      return undefined;
    },
    ownKeys() {
      return Object.keys(SEVERITY_SLOT);
    },
    getOwnPropertyDescriptor(_target, prop: string) {
      if (prop in SEVERITY_SLOT) {
        return {
          configurable: true,
          enumerable: true,
          writable: false,
          value: getSeverityTint(prop as SeverityType),
        };
      }
      return undefined;
    },
  },
);

export const SEVERITY_ORDER: SeverityType[] = [
  "critical",
  "major",
  "minor",
  "observation",
];

/**
 * Severity options for select dropdowns in finding forms.
 * Used by ManualFindingForm and FindingInlineEditor.
 */
export const SEVERITY_OPTIONS: Array<{
  value: SeverityType;
  labelKey: string;
  label: string;
}> = [
  {
    value: "critical",
    labelKey: "findings.severity.critical",
    label: "Critical",
  },
  { value: "major", labelKey: "findings.severity.major", label: "Major" },
  { value: "minor", labelKey: "findings.severity.minor", label: "Minor" },
  {
    value: "observation",
    labelKey: "findings.severity.observation",
    label: "Observation",
  },
];

/**
 * Returns finding status style derived from palette.
 * - draft: audit-status-1 (Pending slot)
 * - open: deadlines-1 (red-toned, urgency)
 * - resolved: deadlines-3 (green-toned, OK)
 */
function getStatusStyle(status: FindingStatusType): StatusConfig {
  const STATUS_PALETTE: Record<
    FindingStatusType,
    { area: string; slot: number; labelKey: string; label: string }
  > = {
    draft: {
      area: "audit-status",
      slot: 1,
      labelKey: "findings.status.draft",
      label: "Draft",
    },
    open: {
      area: "deadlines",
      slot: 1,
      labelKey: "findings.status.open",
      label: "Open",
    },
    resolved: {
      area: "deadlines",
      slot: 3,
      labelKey: "findings.status.resolved",
      label: "Resolved",
    },
  };
  const cfg = STATUS_PALETTE[status];
  const color = getPaletteColor(cfg.area, cfg.slot);
  return {
    bg: `${color}20`, // ~12% opacity background
    text: color,
    labelKey: cfg.labelKey,
    label: cfg.label,
  };
}

export const STATUS_STYLES: Record<FindingStatusType, StatusConfig> = new Proxy(
  {} as Record<FindingStatusType, StatusConfig>,
  {
    get(_target, prop: string) {
      if (prop === "draft" || prop === "open" || prop === "resolved") {
        return getStatusStyle(prop);
      }
      return undefined;
    },
    ownKeys() {
      return ["draft", "open", "resolved"];
    },
    getOwnPropertyDescriptor(_target, prop: string) {
      if (prop === "draft" || prop === "open" || prop === "resolved") {
        return {
          configurable: true,
          enumerable: true,
          writable: false,
          value: getStatusStyle(prop),
        };
      }
      return undefined;
    },
  },
);

// ─── Hooks (translated labels) ───────────────────────────────────────────────

/**
 * Hook that returns severity color config with translated labels.
 * Mirrors the pattern from ChecklistRow's `useChecklistStatusConfig()`.
 */
export function useSeverityConfig(): Record<SeverityType, SeverityConfig> {
  const { t } = useTranslation("audits");
  return {
    critical: {
      ...SEVERITY_COLORS.critical,
      label: t(
        SEVERITY_COLORS.critical.labelKey as "findings.severity.critical",
      ) as string,
    },
    major: {
      ...SEVERITY_COLORS.major,
      label: t(
        SEVERITY_COLORS.major.labelKey as "findings.severity.major",
      ) as string,
    },
    minor: {
      ...SEVERITY_COLORS.minor,
      label: t(
        SEVERITY_COLORS.minor.labelKey as "findings.severity.minor",
      ) as string,
    },
    observation: {
      ...SEVERITY_COLORS.observation,
      label: t(
        SEVERITY_COLORS.observation.labelKey as "findings.severity.observation",
      ) as string,
    },
  };
}

/**
 * Hook that returns status style config with translated labels.
 */
export function useStatusConfig(): Record<FindingStatusType, StatusConfig> {
  const { t } = useTranslation("audits");
  return {
    draft: {
      ...STATUS_STYLES.draft,
      label: t(
        STATUS_STYLES.draft.labelKey as "findings.status.draft",
      ) as string,
    },
    open: {
      ...STATUS_STYLES.open,
      label: t(STATUS_STYLES.open.labelKey as "findings.status.open") as string,
    },
    resolved: {
      ...STATUS_STYLES.resolved,
      label: t(
        STATUS_STYLES.resolved.labelKey as "findings.status.resolved",
      ) as string,
    },
  };
}

/**
 * Hook that returns severity options with translated labels for dropdowns.
 */
export function useSeverityOptions(): Array<{
  value: SeverityType;
  label: string;
}> {
  const { t } = useTranslation("audits");
  return SEVERITY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey as "findings.severity.critical") as string,
  }));
}

/**
 * Hook that returns status filter options with translated labels.
 */
export function useStatusOptions(): Array<{
  value: FindingStatusType;
  label: string;
}> {
  const { t } = useTranslation("audits");
  return [
    {
      value: "open",
      label: t(STATUS_STYLES.open.labelKey as "findings.status.open") as string,
    },
    {
      value: "resolved",
      label: t(
        STATUS_STYLES.resolved.labelKey as "findings.status.resolved",
      ) as string,
    },
  ];
}
